import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, useReducedMotion } from "motion/react";
import { Bolt } from "@/components/brand/Bolt";

export interface AuroraBlobProps {
  /** 0..1 active-speaker audio level. */
  level: number;
  /** Whether any speaker is active — brightens/glows the aura. */
  speaking: boolean;
  /** Whether the assistant (not the user) is speaking — drives Bolt's mouth. */
  talking?: boolean;
}

/** Soft morphing emerald aura that reacts to live audio, with the Bolt
 *  character in front (talking while the assistant speaks). Reduced motion →
 *  static. Uses transform/opacity only (no layout thrash). */
export function AuroraBlob({ level, speaking, talking = false }: AuroraBlobProps) {
  const reduce = useReducedMotion() ?? false;
  const theme = useTheme();
  const clamped = Math.min(Math.max(level, 0), 1);
  const scale = reduce ? 1 : 1 + clamped * 0.35;
  // Brand green → caramel aura. `tint` brightens when speaking.
  const tint = speaking ? "#4caf7d" : "#2d7a4f";
  return (
    <Box sx={{ position: "relative", width: 168, height: 168, display: "grid", placeItems: "center" }}>
      {/* Audio-reactive aura behind the character (blurred to a glow halo). */}
      <motion.div
        aria-hidden
        animate={reduce ? {} : { scale }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        style={{
          position: "absolute",
          width: 148,
          height: 148,
          borderRadius: "46% 54% 60% 40% / 50% 46% 54% 50%",
          background: `linear-gradient(135deg, ${tint}, #2d7a4f 50%, #cd8e54)`,
          boxShadow: `0 0 ${60 + clamped * 60}px rgba(45,122,79,${0.4 + clamped * 0.4})`,
          filter: "blur(7px)",
          opacity: 0.92,
        }}
      />
      {/* Bolt on a soft surface plate so the character reads over the aura. */}
      <Box
        sx={{
          position: "relative",
          width: 108,
          height: 108,
          borderRadius: "50%",
          bgcolor: "background.default",
          display: "grid",
          placeItems: "center",
          boxShadow:
            theme.palette.mode === "light"
              ? "0 10px 30px rgba(20,20,18,0.14)"
              : "0 12px 34px rgba(0,0,0,0.42)",
        }}
      >
        <Bolt size={76} state={talking ? "talking" : "idle"} decorative />
      </Box>
    </Box>
  );
}
